import { v4 } from "uuid";
import type { FormFieldType, FormStateType, UploadFormField } from "../types";
import type { S3StagedFile, SerializedFile } from "./types";
import { FileSize } from "./types";
import { addError } from "../tools";
import { ALL_FILE_UPLOAD_FIELDS } from "../constants";
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILENAME_SIZE,
  WC_ALL_FILES_MAX_SIZE,
  WC_SINGLE_FILE_MAX_SIZE,
  CARGO_ALL_FILES_MAX_SIZE,
  CARGO_SINGLE_FILE_MAX_SIZE,
} from "./constants";
import { uploadFileService } from "src/api/files/uploadFile";
import { UPLOAD_BUCKET } from "src/views/workersCompensationDocuments/constants";
import { AttachmentInput } from "@amzn/ttechclaimintakeservice-client";
import { LineOfBusiness } from "../../app/types";

/**
 * Converts bytes to the specified file size unit.
 *
 * @export
 * @param {number} bytes - The number of bytes to convert.
 * @param {FileSize} unit - The target file size unit to convert to.
 * @returns {number} The converted file size in the specified unit.
 *
 */
export const convertBytesTo = (bytes: number, unit: FileSize): number => {
  switch (unit) {
    // Decimal
    case FileSize.KB: {
      return bytes / 1000;
    }
    case FileSize.MB: {
      return bytes / 1000 ** 2;
    }
    case FileSize.GB: {
      return bytes / 1000 ** 3;
    }
    // Binary
    case FileSize.KIB: {
      return bytes / 1024;
    }
    case FileSize.MIB: {
      return bytes / 1024 ** 2;
    }
    case FileSize.GIB: {
      return bytes / 1024 ** 3;
    }
    // Default
    case FileSize.BYTES:
    default: {
      return bytes;
    }
  }
};

/**
 * Checks the total size of all files and add an error if it exceeds the maximum allowed size
 * @param {File[]} files - All the files to be checked
 * @param {string[]} errors - An array to store error messages that will be used by the file attachment field
 * @param {number} size - Size in bytes to validate

 */
const checkTotalFilesSize = (files: File[], errors: string[], size: number) => {
  let totalSize = 0;

  files.forEach((file: File) => {
    if (file != null && "size" in file) {
      totalSize = totalSize + file.size;
    }
  });
  if (totalSize >= size) {
    const sizeInMB = Math.round(convertBytesTo(size, FileSize.MB));
    addError(`Total combined files size can't exceed ${sizeInMB}MB`, errors);
  }
};

/**
 * Creates a new file with a new name
 * @param {File} originalFile - The original file to be renamed
 * @param {string} newName - The new name for the file
 * @returns {File} - A new file with the new name
 */
const renameFile = (originalFile: File, newName: string) => {
  return new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
};

/**
 * Replaces all space separators in file names with normal spaces to avoid
 * issues with file names when uploading files.
 * @param {File[]} files - An array of files
 * @returns {File[]} - An array of files with modified names
 */
export const replaceSpaceSeparatorsWithNormalSpace = (files: File[]) => {
  const modifiedFiles: File[] = [];
  for (const file of files) {
    // eslint-disable-next-line no-useless-escape
    const fileName = file.name.replaceAll(/\s/g, " ");
    modifiedFiles.push(renameFile(file, fileName));
  }
  return modifiedFiles;
};

/**
 * Checks the file size and add an error if it exceeds the maximum allowed size
 * @param {File} file - The file to be checked
 * @param {string[]} errors - An array to store error messages that will be used by the file attachment field
 * @param {number} size - Size in bytes to validate

 */
export const checkFileSize = (file: File, errors: string[], size: number) => {
  if (file.size >= size) {
    const sizeInMB = Math.round(convertBytesTo(size, FileSize.MB));
    addError(`File size can't exceed ${sizeInMB}MB`, errors);
  }
};

/**
 * Checks the file type agains the list of accepted file types defined in ACCEPTED_FILE_TYPES
 * @param {File} file - The file to be checked
 * @param {string[]} errors - An array to store error messages that will be used by the file attachment field
 */
export const checkFileTypes = (
  file: File,
  errors: string[],
  acceptedFileTypes = ACCEPTED_FILE_TYPES
) => {
  const extension: string | undefined = file.name.toLowerCase().split(".").pop();
  if (!extension || !acceptedFileTypes?.includes(extension)) {
    addError("Unsupported file type", errors);
  }
};

/**
 * Checks that the file name does not exceed the maximum amount of characters allowed defined
 * in MAX_FILENAME_SIZE
 * @param {File} file - The file to be checked
 * @param {string[]} errors - An array to store error messages that will be used by the file attachment field
 */
export const checkFileNameSize = (file: File, errors: string[]) => {
  if (file.name.length > MAX_FILENAME_SIZE) {
    addError(`File name cannot be more than ${MAX_FILENAME_SIZE} characters long`, errors);
  }
};

/**
 * Generates an S3 UUID key that will get used as the filename to store in S3 when uploading files
 * @param {File} file - The file to be uploaded
 * @param {string} s3KeyPrefix - The per-claim key prefix the file is stored under. The legacy
 * forms pass the `amazonClaimReferenceId`; the JSON Forms flow passes an `s3AttachmentsId`
 * because the backend only mints the claim reference id once the claim is created.
 * @returns {string} - The S3 key for the file
 */
export const generateS3Key = (file: File, s3KeyPrefix: string) => {
  const extension: string | undefined = file.name.split(".").pop();
  return `${s3KeyPrefix}/${v4()}.${extension}`;
};

/**
 * Checks for duplicate files in the list of files to be uploaded
 * @param {File[]} files - The list of files to be uploaded
 * @param {File} fileToCheck - The file to check for duplicates
 * @param {string[]} errors - An array to store error messages that will be used by the file attachment field
 */
export const checkDuplicateFiles = (files: File[], fileToCheck: File, errors: string[]) => {
  const duplicates = files.filter((file: File) => file.name === fileToCheck.name);
  if (duplicates.length > 1) {
    addError("You are attempting to upload what appears to be a duplicate file", errors);
  }
};

/**
 * Prepares the files to be stored in the field state. This is required because Redux
 * store cannot have non-serializable objects like File objects, so we need to convert
 * the File objects to SerializedFile objects before storing them in the field state.
 * SerializedFile is a simple object that contains the necessary information to display
 * the file in the UI
 * @param {S3StagedFile[]} files - The list of files to be uploaded
 * @param {SerializedFile[]} existingFiles - Files already stored for this field
 * @param {string} s3KeyPrefix - The per-claim key prefix used to generate the S3 key
 * @returns {SerializedFile[]} - The list of files to be stored in the field state
 */
export const prepareFilesForStorage = (
  files: S3StagedFile[],
  existingFiles: SerializedFile[],
  s3KeyPrefix: string
): SerializedFile[] => {
  return files.map((file: S3StagedFile) => {
    const fileFound = existingFiles?.find((serializedFile: SerializedFile) => {
      return file.name === serializedFile.name;
    });
    if (fileFound) {
      return fileFound;
    }
    return {
      s3Key: file.s3Key || generateS3Key(file, s3KeyPrefix),
      name: file.name,
      lastModified: file.lastModified,
      type: file.type,
      size: file.size,
      webkitRelativePath: file.webkitRelativePath,
    };
  });
};

/**
 * Adds the S3 key to each file in the list of files
 * @param {File[]} files - The list of files to be uploaded
 * @param {string} s3KeyPrefix - The per-claim key prefix used to generate the S3 key
 * @returns {S3StagedFile[]} - The list of files with the S3 key added
 */
export const convertFilesToS3StagedFiles = (files: File[], s3KeyPrefix: string) => {
  const s3StagedFiles: S3StagedFile[] = [];
  files.forEach((file: File) => {
    const s3File: S3StagedFile = file;
    s3File.s3Key = generateS3Key(file, s3KeyPrefix);
    s3StagedFiles.push(s3File);
  });
  return s3StagedFiles;
};

/**
 * Selects the files that are not already stored to avoid running
 * validations and uploading them again.
 * @param {File[]} files - The list of files to be uploaded
 * @param {SerializedFile[]} existingFiles - Files already stored for this field
 * @returns {File[]} - The list of files that are not already stored
 */
export const selectFilesForUpload = (files: File[], existingFiles: SerializedFile[]) => {
  return files.filter((file: File) => {
    const fileFound = existingFiles?.find(
      (serializedFile: SerializedFile) => serializedFile.name === file.name
    );
    // If file is not already stored, return it for upload
    return !fileFound;
  });
};

/**
 * This function uploads a file to S3 using the uploadFile service
 * it requires that files have already been processed to add the s3key to them.
 * @param {S3StagedFile} file - The file to be uploaded
 * @returns {Promise<void>} - A promise that resolves when the file is uploaded
 */
export const uploadFile = async (file: S3StagedFile, bucketType: string) => {
  return await uploadFileService(
    {
      key: file.s3Key,
      contentLength: file.size,
      fileType: bucketType,
    },
    file
  );
};

/**
 * Converts the files stored in the field state to File objects so that they
 * can be used by the FileUpload component.
 * @param {SerializedFile[]} serializedFiles - The list of files to be converted
 * @returns {File[]} - The list of files converted from SerializedFile to File
 */
export const transformSerializedFilesToFiles = (serializedFiles: SerializedFile[]) => {
  const result: File[] = [];
  if (!serializedFiles.length) {
    return result;
  }
  serializedFiles.forEach((file: SerializedFile) => {
    result.push(new File([new ArrayBuffer(file.size)], file.name, { ...file }));
  });
  return result;
};

/**
 * Runs all the validations for the files to be uploaded
 * @param {File[]} files - The list of files to be uploaded
 * @param {string[]} errors - An array to store error messages that will be used by the file attachment field
 * @param {lineOfBusiness} lineOfBusiness - Line of business, can be WORKERS_COMPENSATION, CARGO or IPL
 * @param {string} acceptedFileTypes - String containing the list of accepted file types separated by commas
 */
export const validateUploadedFiles = (
  files: File[],
  errors: string[],
  lineOfBusiness: LineOfBusiness,
  acceptedFileTypes?: string
) => {
  const maxTotalFileSize =
    lineOfBusiness === LineOfBusiness.CARGO ? CARGO_ALL_FILES_MAX_SIZE : WC_ALL_FILES_MAX_SIZE;

  const singleMaxFileSize =
    lineOfBusiness === LineOfBusiness.CARGO ? CARGO_SINGLE_FILE_MAX_SIZE : WC_SINGLE_FILE_MAX_SIZE;

  checkTotalFilesSize(files, errors, maxTotalFileSize);
  for (const file of files) {
    checkFileTypes(file, errors, acceptedFileTypes);
    checkFileSize(file, errors, singleMaxFileSize);
    checkFileNameSize(file, errors);
    checkDuplicateFiles(files, file, errors);
    if (errors.length) {
      return;
    }
  }
};

/**
 * Runs all the validations for the files to be uploaded
 * @param {File[]} files - The list of files to be uploaded
 * @param {string[]} errors - An array to store error messages that will be used by the file attachment field
 */
export const getFilesFromOtherFields = (field: FormFieldType, formState: FormStateType) => {
  const files: SerializedFile[] = [];
  Object.entries(formState).forEach(([key, fieldState]) => {
    if (ALL_FILE_UPLOAD_FIELDS.includes(key) && key !== field.config.name) {
      files.push(...fieldState.value);
    }
  });
  return transformSerializedFilesToFiles(files);
};

/** Input to {@link validateAndUploadFilesToS3}. */
export interface ValidateAndUploadFilesToS3Params {
  /** The files currently selected in the file upload control. */
  inputFiles: File[];
  /** Files already uploaded and stored for this field. */
  existingFiles: SerializedFile[];
  /** Per-claim S3 key prefix all of this claim's attachments share. */
  s3KeyPrefix: string;
  lineOfBusiness: LineOfBusiness;
  /**
   * Files held by sibling upload fields. Included so the combined-size cap
   * spans every attachment on the claim rather than a single field.
   */
  otherFieldFiles?: File[];
  /** Comma-separated extension allowlist. Defaults to `ACCEPTED_FILE_TYPES`. */
  acceptedFileTypes?: string;
}

/** Result of {@link validateAndUploadFilesToS3}. */
export interface ValidateAndUploadFilesToS3Result {
  /** The files that should now be stored for the field. */
  files: SerializedFile[];
  /** Validation or upload errors. Empty when everything succeeded. */
  errors: string[];
}

/**
 * Validates a file selection and uploads whatever is new to S3, returning the
 * files to store and any errors. Storage-agnostic: callers decide where the
 * result lands (legacy field state, JSON Forms data, ...).
 *
 * Files are keyed under `s3KeyPrefix` so every attachment on a claim shares a
 * folder. The legacy forms pass the `amazonClaimReferenceId`; the JSON Forms
 * flow passes an `s3AttachmentsId` because the backend mints the claim
 * reference id only once the claim is created.
 *
 * Uploads run sequentially and stop at the first failure so a partial batch
 * does not leave the stored list disagreeing with what is actually in S3.
 */
export const validateAndUploadFilesToS3 = async ({
  inputFiles,
  existingFiles,
  s3KeyPrefix,
  lineOfBusiness,
  otherFieldFiles = [],
  acceptedFileTypes,
}: ValidateAndUploadFilesToS3Params): Promise<ValidateAndUploadFilesToS3Result> => {
  const errors: string[] = [];
  const files = replaceSpaceSeparatorsWithNormalSpace(inputFiles);

  // Fewer files than stored means the user removed one. Nothing to validate
  // or re-upload; just narrow the stored list to what is left.
  if (files.length < existingFiles.length) {
    return { files: prepareFilesForStorage(files, existingFiles, s3KeyPrefix), errors };
  }

  validateUploadedFiles([...otherFieldFiles, ...files], errors, lineOfBusiness, acceptedFileTypes);
  if (errors.length) {
    return { files: existingFiles, errors };
  }

  const filesForUpload = selectFilesForUpload(files, existingFiles);
  const s3StagedFiles = convertFilesToS3StagedFiles(filesForUpload, s3KeyPrefix);
  const bucketType = UPLOAD_BUCKET[lineOfBusiness];
  for (const file of s3StagedFiles) {
    try {
      await uploadFile(file, bucketType);
    } catch (error) {
      addError("Error uploading file. Please try again.", errors);
      return { files: existingFiles, errors };
    }
  }

  return {
    files: [...existingFiles, ...prepareFilesForStorage(s3StagedFiles, existingFiles, s3KeyPrefix)],
    errors,
  };
};

/**
 * Validates and uploads the files to S3 and updates the field state.
 *
 * Legacy-forms entry point: adapts the shared
 * {@link validateAndUploadFilesToS3} core to the mutable field-state contract
 * the dynamic form renderer expects.
 *
 * @param {File[]} inputFiles - The list of files to be uploaded
 * @param {FormFieldType} field - The field object
 * @param {string} amazonClaimReferenceId - The claim reference id used to generate the S3 key
 * @param {FormStateType} formState - The form state object
 * @param {lineOfBusiness} lineOfBusiness - Line of business, can be WORKERS_COMPENSATION, CARGO or IPL

 * @returns {FormFieldStateType} - The updated field state object
 */
export const validateAndUploadFiles = async (
  inputFiles: File[],
  field: FormFieldType,
  amazonClaimReferenceId: string,
  formState: FormStateType,
  lineOfBusiness: LineOfBusiness
) => {
  const uploadFieldConfig = field.config as UploadFormField;
  const { files, errors } = await validateAndUploadFilesToS3({
    inputFiles,
    existingFiles: field.state.value,
    s3KeyPrefix: amazonClaimReferenceId,
    lineOfBusiness,
    otherFieldFiles: getFilesFromOtherFields(field, formState),
    acceptedFileTypes: uploadFieldConfig.acceptedFileTypes,
  });
  field.state.value = files;
  field.state.errors = errors;
  return field.state;
};

/**
 * Builds attachment input array from staged files
 * @param value - Array of staged or stored files carrying their S3 key
 * @returns AttachmentInput[] - Array of processed file attachments
 */
export const buildAttachments = (value: (S3StagedFile | SerializedFile)[]): AttachmentInput[] => {
  const fileAttachments: AttachmentInput[] = [];

  value.forEach((file) => {
    const attachment = {
      fileExtension: file.name.split(".").pop(),
      s3Key: file.s3Key,
      fileName: file.name,
      fileSize: file.size,
    };

    fileAttachments.push(attachment);
  });

  return fileAttachments;
};
